import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ApiConfiguration } from '../../../core/api/api-configuration';
import { ChangeLogModel, SparkLogEntityType } from '../../../core/api/models';

export interface ParsedChangeLog {
  Action: string;
  [key: string]: any;
}

type ChangeLogFilter = 'all' | 'status';

interface ParsedChangeLogEntry {
  log: ChangeLogModel;
  parsedData: ParsedChangeLog | null;
}

@Component({
  selector: 'app-change-log-modal',
  templateUrl: './change-log-modal.component.html',
  styleUrl: './change-log-modal.component.css',
  standalone: false,
})
export class ChangeLogModalComponent implements OnInit, OnDestroy {
  @Input() entityID: number | null = null;
  @Input() entityType: SparkLogEntityType = SparkLogEntityType.Program;
  @Input() entityName: string = '';
  @Input() defaultFilter: ChangeLogFilter = 'all';
  @Input() showFilterToggle = false;

  changeLogs = signal<ChangeLogModel[]>([]);
  isLoading = signal<boolean>(false);
  isVisible = signal<boolean>(false);
  activeFilter = signal<ChangeLogFilter>('all');

  private loadSubscription: Subscription | null = null;
  private loadSequence = 0;

  constructor(private http: HttpClient, private apiConfig: ApiConfiguration) {}

  ngOnInit(): void {
    this.activeFilter.set(this.defaultFilter);
    // Load only when opened — avoids racing preload responses overwriting results.
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }

  open(): void {
    this.isVisible.set(true);
    this.activeFilter.set(this.defaultFilter);
    this.loadChangeLogs();
  }

  close(): void {
    this.isVisible.set(false);
  }

  loadChangeLogs(): void {
    if (!this.entityID) return;

    const requestEntityID = this.entityID;
    const requestEntityType = Number(this.entityType);
    if (!Number.isFinite(requestEntityType) || requestEntityType <= 0) return;

    const sequence = ++this.loadSequence;
    this.loadSubscription?.unsubscribe();
    this.isLoading.set(true);

    const url = `${this.apiConfig.rootUrl}/api/Supervisor/GetChangeLog?entityID=${requestEntityID}&entityTypeID=${requestEntityType}`;

    this.loadSubscription = this.http.get<ChangeLogModel[]>(url).subscribe({
      next: (logs: ChangeLogModel[]) => {
        if (sequence !== this.loadSequence) return;
        if (requestEntityID !== this.entityID) return;

        this.changeLogs.set(Array.isArray(logs) ? logs : []);
        this.isLoading.set(false);
      },
      error: (error) => {
        if (sequence !== this.loadSequence) return;
        console.error('Error loading change logs:', error);
        this.isLoading.set(false);
      },
    });
  }

  parseChangeData(data: string | null | undefined): ParsedChangeLog | null {
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  private getLogData(log: ChangeLogModel): string {
    const anyLog = log as ChangeLogModel & { data?: string | null };
    return anyLog.Data || anyLog.data || '';
  }

  get parsedLogs(): ParsedChangeLogEntry[] {
    return this.changeLogs().map((log) => ({
      log,
      parsedData: this.parseChangeData(this.getLogData(log)),
    }));
  }

  get displayedLogs(): ParsedChangeLogEntry[] {
    if (this.activeFilter() === 'status') {
      return this.parsedLogs.filter((entry) =>
        this.isStatusEvent(entry.parsedData)
      );
    }
    return this.parsedLogs;
  }

  setFilter(filter: ChangeLogFilter): void {
    this.activeFilter.set(filter);
  }

  hasStatusEvents(): boolean {
    return this.parsedLogs.some((entry) =>
      this.isStatusEvent(entry.parsedData)
    );
  }

  shouldShowFilterToggle(): boolean {
    return this.showFilterToggle && this.hasStatusEvents();
  }

  isStatusEvent(parsedData: ParsedChangeLog | null): boolean {
    return (
      parsedData?.Action === 'Status Changed' &&
      !!parsedData?.['PatientTargetStatusID']
    );
  }

  getEmptyMessage(): string {
    if (this.activeFilter() === 'status') {
      return 'No status history available for this item.';
    }
    return 'No change history available for this item.';
  }

  formatPropertyName(name: string): string {
    if (name === 'PatientTargetStatusID') return 'Status';
    if (name === 'PatientSessionID') return 'Session';
    if (name === 'ResponsePercentage') return 'Response Percentage';
    if (name === 'ConsecutiveDays') return 'Consecutive Days';
    if (name === 'FirstTrialMustBeIndependent')
      return 'First Trial Must Be Independent';
    if (name === 'ShouldMasterAutomatic') return 'Mastery Calculation';
    if (name === 'ShouldMoveToMaintenanceAfterMastery') return 'After Mastery';
    if (name === 'MaintenanceCriteriaID') return 'Maintenance Criteria';
    if (name === 'CurrentPromptCodeID') return 'Prompt Level';
    if (name === 'Attachment') return 'Attachment';
    // Convert camelCase/PascalCase to Title Case
    if (name === 'DetachedSubTargets') return 'Detached Sub Targets';
    if (name === 'ParentTargetName') return 'Parent Target';
    return name
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  formatValue(value: any): string {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : 'None';
      }
      if (value.Old !== undefined || value.New !== undefined) {
        return `From: ${this.formatSingleValue(
          value.Old
        )} → To: ${this.formatSingleValue(value.New)}`;
      }
      return JSON.stringify(value);
    }
    return String(value);
  }

  formatSingleValue(value: any): string {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : 'None';
      }
      return JSON.stringify(value);
    }
    return String(value);
  }

  getUserName(log: ChangeLogModel): string {
    return log.UserFullName || `User #${log.UserID}`;
  }

  getChangeKeys(parsedData: ParsedChangeLog): string[] {
    if (this.isStatusEvent(parsedData)) {
      return [
        'PatientTargetStatusID',
        'MaintenanceCriteriaID',
        'Source',
        'PatientSessionID',
        'Note',
      ].filter(
        (key) => parsedData[key] !== undefined && parsedData[key] !== null
      );
    }

    const action = parsedData.Action;

    if (action === 'AttachedAsSubTarget' || action === 'DetachedFromParent') {
      return ['TargetName', 'ParentTargetName'].filter(
        (key) => parsedData[key] !== undefined && parsedData[key] !== null
      );
    }

    if (action === 'SubTargetsDetached') {
      return ['TargetName', 'DetachedSubTargets'].filter(
        (key) => parsedData[key] !== undefined && parsedData[key] !== null
      );
    }

    const simpleActions = [
      'Created',
      'Deleted',
      'Restored',
      'Archived',
      'Unarchived',
    ];

    if (simpleActions.includes(action)) {
      return Object.keys(parsedData).filter(
        (key) =>
          key !== 'Action' &&
          !(key === 'IsArchived' && typeof parsedData[key] === 'boolean')
      );
    }

    return Object.keys(parsedData).filter((key) => key !== 'Action');
  }

  getActionColor(action: string): string {
    switch (action) {
      case 'Status Changed':
        return '#6f42c1';
      case 'Created':
        return '#28a745';
      case 'Updated':
        return '#007bff';
      case 'Deleted':
        return '#dc3545';
      case 'Restored':
        return '#ffc107';
      case 'Archived':
        return '#6c757d';
      case 'Unarchived':
        return '#17a2b8';
      case 'SubTargetsDetached':
      case 'DetachedFromParent':
        return '#fd7e14';
      case 'AttachedAsSubTarget':
        return '#20c997';
      default:
        return '#719eb6';
    }
  }

  getActionIcon(action: string): string {
    switch (action) {
      case 'Status Changed':
        return 'pi pi-arrow-right';
      case 'Created':
        return 'pi pi-plus';
      case 'Updated':
        return 'pi pi-pencil';
      case 'Deleted':
        return 'pi pi-trash';
      case 'Restored':
        return 'pi pi-undo';
      case 'Archived':
        return 'pi pi-folder';
      case 'Unarchived':
        return 'pi pi-refresh';
      case 'SubTargetsDetached':
      case 'DetachedFromParent':
        return 'pi pi-link';
      case 'AttachedAsSubTarget':
        return 'pi pi-sitemap';
      default:
        return 'pi pi-info-circle';
    }
  }

  formatFieldValue(fieldName: string, value: any): string {
    if (fieldName === 'PatientSessionID' && value != null) {
      return `#${value}`;
    }

    if (
      typeof value === 'object' &&
      !Array.isArray(value) &&
      (value.Old !== undefined || value.New !== undefined)
    ) {
      const oldVal = this.formatFieldSpecificValue(fieldName, value.Old);
      const newVal = this.formatFieldSpecificValue(fieldName, value.New);
      return `<span class="old-value">${oldVal}</span> → <span class="new-value">${newVal}</span>`;
    }

    return this.formatFieldSpecificValue(fieldName, value);
  }

  getStatusSummary(parsedData: ParsedChangeLog | null): string | null {
    if (!this.isStatusEvent(parsedData)) return null;

    const statusChange = parsedData?.['PatientTargetStatusID'];
    if (
      statusChange &&
      typeof statusChange === 'object' &&
      statusChange.Old !== undefined &&
      statusChange.New !== undefined
    ) {
      return `${this.formatSingleValue(statusChange.Old)} -> ${this.formatSingleValue(statusChange.New)}`;
    }

    return null;
  }

  formatChangeValue(value: any): string {
    if (value === null || value === undefined) return 'null';

    if (typeof value === 'object' && !Array.isArray(value)) {
      if (value.Old !== undefined || value.New !== undefined) {
        const oldVal = this.formatSingleValue(value.Old);
        const newVal = this.formatSingleValue(value.New);
        return `<span class="old-value">${oldVal}</span> → <span class="new-value">${newVal}</span>`;
      }
      if (
        value.Old &&
        Array.isArray(value.Old) &&
        value.New &&
        Array.isArray(value.New)
      ) {
        const added = value.New.filter((v: any) => !value.Old.includes(v));
        const removed = value.Old.filter((v: any) => !value.New.includes(v));
        let result = '';
        if (added.length > 0) {
          result += `<span class="added">Added: ${added.join(', ')}</span>`;
        }
        if (removed.length > 0) {
          if (result) result += ' | ';
          result += `<span class="removed">Removed: ${removed.join(
            ', '
          )}</span>`;
        }
        return result || 'No changes';
      }
      return JSON.stringify(value);
    }

    return this.formatValue(value);
  }

  formatFieldSpecificValue(fieldName: string, value: any): string {
    if (fieldName === 'ShouldMasterAutomatic') {
      return value ? 'Automatic' : 'Manual';
    }

    if (fieldName === 'ShouldMoveToMaintenanceAfterMastery') {
      if (value === null || value === undefined) {
        return 'Manual (no automatic progression)';
      }
      return value ? 'Maintenance' : 'Fluent';
    }

    if (fieldName === 'MaintenanceCriteriaID') {
      if (value == null) {
        return 'Default criteria';
      }

      return typeof value === 'number' ? `#${value}` : String(value);
    }

    return this.formatChangeValue(value);
  }
}
